import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { api, ApiError, setToken, setUnauthorizedHandler, TOKEN_KEY } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";

export const GOOGLE_ERR = "Google sign-in didn't complete";

const PENDING = "lr_google_pending";
let LANDING_URL = `${window.location.hash}${window.location.search}`;

const Ctx = createContext(null);

const store = {
  get: () => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (v) => {
    try {
      localStorage.setItem(TOKEN_KEY, v);
    } catch {
      /* storage blocked: session lasts for this tab only */
    }
  },
  remove: () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* storage blocked */
    }
  },
};

export function AuthProvider({ children }) {
  const [state, setState] = useState({ status: "loading", user: null, googleError: null });

  const finish = useCallback((res) => {
    setToken(res.token);
    store.set(res.token);
    setState({ status: "in", user: res.user, googleError: null });
  }, []);

  const clear = useCallback((googleError = null) => {
    setToken(null);
    store.remove();
    queryClient.clear();
    setState({ status: "out", user: null, googleError });
  }, []);

  const exchangeGoogleSession = useCallback(
    async (sessionId) => {
      try {
        finish(await api("/auth/google/session", { method: "POST", body: { session_id: sessionId } }));
      } catch {
        clear(GOOGLE_ERR);
        throw new Error(GOOGLE_ERR);
      }
    },
    [finish, clear],
  );

  useEffect(() => {
    setUnauthorizedHandler(() => clear());
    (async () => {
      // Returning from Emergent Google auth with #session_id=... -> process FIRST
      // (read from the URL captured at module load: child <Navigate> effects run before this one and drop the hash)
      const m = LANDING_URL.match(/session_id=([^&#]+)/);
      const pending = sessionStorage.getItem(PENDING);
      sessionStorage.removeItem(PENDING);
      if (m) {
        LANDING_URL = "";
        if (window.location.hash.includes("session_id")) window.history.replaceState(null, "", window.location.pathname);
        await exchangeGoogleSession(m[1]).catch(() => {});
        return;
      }
      const saved = store.get();
      if (!saved) return setState({ status: "out", user: null, googleError: pending ? GOOGLE_ERR : null });
      setToken(saved);
      try {
        finish(await api("/auth/refresh", { method: "POST" })); // sliding session
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) clear();
        else setState({ status: "in", user: null, googleError: null }); // offline: keep the session
      }
    })();
  }, [clear, finish, exchangeGoogleSession]);

  const value = useMemo(
    () => ({
      ...state,
      signIn: async (email, password) => finish(await api("/auth/login", { method: "POST", body: { email, password } })),
      signUp: async (name, email, password) =>
        finish(await api("/auth/register", { method: "POST", body: { name, email, password } })),
      signInWithGoogle: () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = `${window.location.origin}/`;
        sessionStorage.setItem(PENDING, "1");
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      },
      signOut: async () => {
        await api("/auth/logout", { method: "POST" }).catch(() => {});
        clear();
      },
    }),
    [state, finish, clear],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
